import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WatchAlert } from '@compumark/order-management-client';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { OrdersApiClientService } from 'src/app/services/orders.api.client.service';

import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { AddWatchOrderDialogComponent } from '../../add-watch-order-dialog/add-watch-order-dialog.component';
import { AdjustExistingHitOverviewComponent } from '../../adjust-existing-hit-overview/adjust-existing-hit-overview.component';
import { OrderActionsDialogComponent } from '../../order-actions-dialog/order-actions-dialog.component';
import { AlertApiClientService } from '../../services/alert-api-client.service';
import { AlertGridOptionsService } from '../alert-column-definition/alert-grid-options.service';

@Component({
  selector: 'app-panel-view-row-renderer',
  templateUrl: './panel-view-row-renderer.component.html',
  styleUrls: ['./panel-view-row-renderer.component.scss'],
  standalone: false,
})
export class PanelViewRowRendererComponent
  implements ICellRendererAngularComp, OnDestroy
{
  private orderApiClient = inject(OrdersApiClientService);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private alertApiClientService = inject(AlertApiClientService);
  private alertGridOptionsService = inject(AlertGridOptionsService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  params!: ICellRendererParams;
  loading = false;
  readonly PANEL_ROW_CONTENT_OFFSET = 85;
  public readonly CLIENT_REFERENCE_LABEL = 'Links to Your Reference';
  public readonly SUB_CLIENT_REFERENCE_LABEL = 'Links to Sub Client Reference';
  actionDisabled = true;
  copyDisabled = true;
  error: string | null = null;
  orders: any[] | null = null;
  public clientReference!: string[];
  public subClientReference!: string[];
  private sub?: Subscription;
  isSystemAlert: boolean | undefined;
  public selectedOrders: any[] = [];
  public selectedOrderIds: string[] = [];
  @ViewChild('panelBody', { static: false }) panelBody?: ElementRef;
  @ViewChild('copyIcon', { static: false }) copyIcon?: ElementRef;
  @ViewChild('moveIcon', { static: false }) moveIcon?: ElementRef;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.isSystemAlert = params.data?.systemGenerated;
    this.clientReference = [...(this.params?.data?.clientReference ?? [])];
    this.subClientReference = [
      ...(this.params?.data?.subClientReference ?? []),
    ];

    const alertId = (params.data &&
      (params.data.originalId || params.data.id)) as string;
    this.fetchReferences(alertId);

    if (alertId && !params.data._panelRowFetched) {
      this.fetchOrders(alertId);
      return;
    }

    this.loading = false;
    this.error = null;
    this.orders = params.data._panelOrders || null;
    this.changeDetectorRef.markForCheck();
  }

  refresh(params: ICellRendererParams): boolean {
    const previousAlertId =
      this.params && (this.params.data?.originalId || this.params.data?.id);
    const nextAlertId =
      params.data && (params.data.originalId || params.data.id);

    this.params = params;
    this.isSystemAlert = params.data?.systemGenerated;

    if (previousAlertId !== nextAlertId) {
      this.loading = false;
      this.error = null;
      this.orders = params.data?._panelOrders || null;
      this.sub?.unsubscribe();
    }

    if (nextAlertId && !params.data._panelRowFetched) {
      this.fetchOrders(nextAlertId);
    } else if (nextAlertId && params.data._panelRowFetched) {
      this.loading = false;
      this.error = null;
      this.orders = params.data._panelOrders || null;
    }

    this.changeDetectorRef.markForCheck();
    setTimeout(() => this.updateRowHeight(), 0);
    return true;
  }

  fetchReferences(alertId: string): void {
    this.alertApiClientService.getWatchAlertById(alertId).subscribe((alert) => {
      this.setReferences(alert.clientReference, alert.subClientReference);
    });
  }

  fetchOrders(alertId: string): void {
    this.sub?.unsubscribe();
    this.loading = true;
    this.error = null;
    this.orders = null;
    this.changeDetectorRef.markForCheck();

    this.sub = this.orderApiClient.getOrdersByAlertId(alertId).subscribe({
      next: (orders) => {
        const currentAlertId =
          this.params?.data &&
          (this.params.data.originalId || this.params.data.id);
        if (currentAlertId && currentAlertId !== alertId) {
          return;
        }

        this.loading = false;
        this.orders = orders;
        if (this.params?.data) {
          this.params.data._panelRowFetched = true;
          this.params.data._panelOrders = orders;
        }
        this.changeDetectorRef.markForCheck();
        setTimeout(() => this.updateRowHeight(), 0);
      },
      error: (err) => {
        const currentAlertId =
          this.params?.data &&
          (this.params.data.originalId || this.params.data.id);
        if (currentAlertId && currentAlertId !== alertId) {
          return;
        }

        this.loading = false;
        this.error = err?.message || 'Failed to load orders';
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  onGridHeightChange(height: number): void {
    if (this.loading && this.params && this.params.api && this.params.node) {
      this.params.node.setRowHeight(
        (this.panelBody?.nativeElement?.offsetHeight ?? height) +
          this.PANEL_ROW_CONTENT_OFFSET,
      );
      this.params.api.onRowHeightChanged();
      return;
    }
    this.updateRowHeight();
  }

  updateRowHeight(): void {
    if (this.panelBody && this.params && this.params.api && this.params.node) {
      this.params.node.setRowHeight(
        this.panelBody?.nativeElement?.offsetHeight +
          this.PANEL_ROW_CONTENT_OFFSET,
      );
      this.params.api.onRowHeightChanged();
    }
  }

  copyOrders(): void {
    this.openBulkActionDialog('copy', this.copyIcon);
  }

  moveOrders(): void {
    this.openBulkActionDialog('move', this.moveIcon);
  }

  private openBulkActionDialog(
    action: 'copy' | 'move',
    iconRef?: ElementRef,
  ): void {
    if (!this.selectedOrders || this.selectedOrders.length === 0) {
      return;
    }

    const validOrders =
      action === 'move'
        ? this.selectedOrderIds
        : this.selectedOrders.map((o) => o.id);

    if (validOrders.length === 0) {
      return;
    }

    const parentGridApi = this.params.api;
    const currentAlertId = this.params.data?.originalId || this.params.data?.id;
    const watchAlerts = this.buildWatchAlerts(parentGridApi, currentAlertId);
    const rect = iconRef?.nativeElement.getBoundingClientRect();

    const dialogRef = this.dialog.open(OrderActionsDialogComponent, {
      width: '16rem',
      data: {
        watchAlerts,
        orderNumber: null,
        sourceAlertId: currentAlertId,
        action,
        params: null,
        parentGridApi,
      },
      backdropClass: 'cdk-overlay-transparent-backdrop',
      hasBackdrop: true,
      panelClass: 'order-actions-dialog-panel',
      position: rect
        ? {
            top: rect.bottom + 'px',
            left: rect.left + 'px',
          }
        : undefined,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.selectedWatchAlert) {
        this.performBulkAction(
          action,
          currentAlertId,
          result.selectedWatchAlert,
          validOrders,
        );
      }
    });
  }

  private buildWatchAlerts(
    parentGridApi: any,
    currentAlertId: string,
  ): { id: string; title: string }[] {
    return this.alertGridOptionsService.getFilteredWatchAlerts(
      parentGridApi,
      currentAlertId,
    );
  }

  private performBulkAction(
    action: 'copy' | 'move',
    sourceAlertId: string,
    targetAlertId: string,
    orderIds: string[],
  ): void {
    const targetAlert = this.buildWatchAlerts(
      this.params.api,
      sourceAlertId,
    ).find((alert) => alert.id === targetAlertId);
    const targetAlertTitle = targetAlert?.title;

    const action$ =
      action === 'copy'
        ? this.alertApiClientService.copyOrdersToWatchAlert(
            targetAlertId,
            orderIds,
          )
        : this.alertApiClientService.moveOrdersBetweenWatchAlerts(
            sourceAlertId,
            targetAlertId,
            orderIds,
          );

    const actionVerb = action === 'copy' ? 'Copied' : 'Moved';

    const validOrdersCount =
      actionVerb === 'Copied'
        ? this.selectedOrders?.length
        : this.selectedOrderIds.length;

    action$.subscribe({
      next: () => {
        this.snackbarService.openHtmlSnackBar(
          [
            { text: `${actionVerb} ` },
            { text: `${validOrdersCount}`, bold: true },
            { text: ` order(s) to "${targetAlertTitle}"` },
          ],
          'Dismiss',
        );
        if (action === 'move') {
          if (this.params?.data?._panelOrders) {
            this.params.data._panelOrders =
              this.params.data._panelOrders.filter(
                (order: any) => !orderIds.includes(order.id),
              );
            this.orders = this.params.data._panelOrders;
            setTimeout(() => this.updateRowHeight(), 0);
          }
        }
        this.alertGridOptionsService.refreshGrid();
      },
    });
  }

  onOrdersSelectionChanged(selectedOrders: any[]): void {
    this.selectedOrders = selectedOrders;
    this.copyDisabled = selectedOrders.length === 0;
    const subClientReferenceGlobal = this.params.data.subClientReference || [];
    const clientReferenceGlobal = this.params.data.clientReference || [];
    const globalSubClientRefSet = new Set(subClientReferenceGlobal);
    const globalClientRefSet = new Set(clientReferenceGlobal);
    const filteredOrders = (this.selectedOrders || []).filter((order: any) => {
      const hasMatchingSubClientRef =
        order.subClientReference &&
        globalSubClientRefSet.has(order.subClientReference);
      const hasMatchingClientRef =
        order.clientReference && globalClientRefSet.has(order.clientReference);

      return !hasMatchingSubClientRef && !hasMatchingClientRef;
    });
    this.selectedOrderIds = filteredOrders
      .map((o: any) => String(o?.id ?? o?.orderId ?? o?._id ?? ''))
      .filter((id: string) => !!id);

    this.actionDisabled = this.selectedOrderIds.length === 0;
  }

  getFilteredAlertCount(): number {
    let count = 0;
    this.params.api.forEachNodeAfterFilterAndSort((node: any) => {
      if (!node.id?.includes('__panel')) {
        count++;
      }
    });
    return count;
  }
  removeWatchOrders(): void {
    if (!this.selectedOrders || this.selectedOrders.length === 0) {
      return;
    }
    const currentSourceAlertId =
      this.params.data?.originalId || this.params.data?.id;
    const currentAlertTitle = this.params.data.title;
    const filteredValidOrdersCount = this.selectedOrderIds.length;
    const filteredRowCount = this.getFilteredAlertCount();
    const shouldClearFilters = filteredRowCount === 1;
    if (!filteredValidOrdersCount) {
      return;
    }
    this.alertApiClientService
      .deleteOrdersFromWatchAlert(currentSourceAlertId, this.selectedOrderIds)
      .subscribe({
        next: () => {
          this.snackbarService.openHtmlSnackBar(
            [
              { text: 'Removed ' },
              { text: String(filteredValidOrdersCount), bold: true },
              { text: ` order(s) from "${currentAlertTitle}"` },
            ],
            'Dismiss',
          );
          if (this.orders) {
            const idsToRemove = new Set(this.selectedOrderIds);
            this.orders = this.orders.filter(
              (o: any) => !idsToRemove.has(String(o?.id ?? '')),
            );
            if (this.params?.data) {
              this.params.data._panelOrders = this.orders;
            }
          }
          const noOrdersLeft = !this.orders || this.orders.length === 0;
          if (shouldClearFilters && noOrdersLeft) {
            this.alertGridOptionsService.clearAlertFilters();
          }

          this.selectedOrders = [];
          this.selectedOrderIds = [];
          this.actionDisabled = true;
          this.copyDisabled = true;
          setTimeout(() => this.updateRowHeight(), 0);
          this.alertGridOptionsService.refreshGrid();
        },
      });
  }

  onAddWatchOrder(): void {
    (document.activeElement as HTMLElement)?.blur();
    const dialogRef = this.dialog.open(AddWatchOrderDialogComponent, {
      data: cloneDeep(this.params.data) as WatchAlert,
      width: '65vw',
      height: '95vh',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((data) => {
      const updatedData = data?.updatedData;
      if (updatedData) {
        this.params.data._panelRowFetched = false;
        this.params.data._panelOrders = null;

        this.alertApiClientService
          .updateAlert({
            ...this.params.data,
            clientReference: updatedData.clientReference,
            subClientReference: updatedData.subClientReference,
            id: this.params.data.originalId ?? this.params.data.id,
            products: Array.isArray(this.params.data.products)
              ? this.params.data.products.join(', ')
              : this.params.data.products,
            orders: Array.isArray(this.params.data.orders)
              ? this.params.data.orders.length
              : this.params.data.orders,
          })
          .subscribe((updated) => {
            this.setReferences(
              updated.clientReference,
              updated.subClientReference,
            );

            if (Array.isArray(updatedData.orders)) {
              this.alertApiClientService
                .setWatchAlertOrders(
                  this.params.data.originalId ?? this.params.data.id,
                  updatedData.orders,
                )
                .subscribe(() => {
                  this.fetchOrders(
                    this.params.data.originalId ?? this.params.data.id,
                  );
                  this.alertGridOptionsService.refreshGrid();
                });
            }
          });
      }
    });
  }

  openAdjustExistingHitOverview(): MatDialogRef<AdjustExistingHitOverviewComponent> {
    return this.dialog.open(AdjustExistingHitOverviewComponent, {
      data: this.params.data,
      width: '60vw',
      height: '82vh',
      disableClose: true,
    });
  }

  setReferences(
    clientReference: string[] | undefined,
    subClientReference: string[] | undefined,
  ): void {
    this.clientReference = clientReference ?? [];
    this.subClientReference = subClientReference ?? [];
    this.params.data.clientReference = this.clientReference;
    this.params.data.subClientReference = this.subClientReference;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
